import {
    ArrowRight20Regular,
    ChevronDown20Regular,
    ChevronUp20Regular,
    PersonChat20Regular
} from "@fluentui/react-icons";
import {FC, useState} from "react";
import type {DiagnosisKpi, DiagnosisReport} from "./intelligenceModels";
import IntelligenceMarkdown from "./IntelligenceMarkdown";

interface IntelligenceDiagnosisCardProps {
    report: DiagnosisReport;
    onLaunchWorkflow: (report: DiagnosisReport) => void;
}

const KPI_SENTIMENT_CLASS: Record<string, string> = {
    positive: "pi-diagnosis-kpi--positive",
    negative: "pi-diagnosis-kpi--negative",
    neutral: "pi-diagnosis-kpi--neutral"
};

const KpiHero: FC<{kpi: DiagnosisKpi}> = ({kpi}) => (
    <div
        className={`pi-diagnosis-kpi ${
            KPI_SENTIMENT_CLASS[kpi.sentiment ?? "neutral"] ?? ""
        }`}
    >
        <span className="pi-diagnosis-kpi__value">{kpi.value}</span>
        <span className="pi-diagnosis-kpi__label">{kpi.label}</span>
        {kpi.context && (
            <span className="pi-diagnosis-kpi__context">{kpi.context}</span>
        )}
    </div>
);

const IntelligenceDiagnosisCard: FC<IntelligenceDiagnosisCardProps> = ({
    report,
    onLaunchWorkflow
}) => {
    const [whyExpanded, setWhyExpanded] = useState(false);

    return (
        <div className="pi-diagnosis">
            <div className="pi-diagnosis__header">
                <span className="pi-diagnosis__category">
                    {report.title} &middot; {report.time_period}
                </span>
                <div className="pi-diagnosis__key-finding">
                    <IntelligenceMarkdown text={report.key_finding} />
                </div>
            </div>

            {report.kpis.length > 0 && (
                <div className="pi-diagnosis__kpis">
                    {report.kpis.map((kpi, index) => (
                        <KpiHero key={index} kpi={kpi} />
                    ))}
                </div>
            )}

            <details
                className="pi-diagnosis__section pi-diagnosis__section--expandable"
                onToggle={(e) =>
                    setWhyExpanded(
                        (e.currentTarget as HTMLDetailsElement).open
                    )
                }
            >
                <summary className="pi-diagnosis__expand-trigger">
                    <span className="pi-diagnosis__label">Why it happened</span>
                    {whyExpanded ? (
                        <ChevronUp20Regular />
                    ) : (
                        <ChevronDown20Regular />
                    )}
                </summary>
                <p className="pi-diagnosis__text pi-diagnosis__text--detail">
                    {report.why_it_happened}
                </p>
            </details>

            <div className="pi-diagnosis__section">
                <span className="pi-diagnosis__label">Why it matters</span>
                <p className="pi-diagnosis__text">{report.why_it_matters}</p>
            </div>

            {report.follow_up_with && (
                <div className="pi-diagnosis__followup">
                    <PersonChat20Regular />
                    <span>
                        <strong>Follow up with:</strong> {report.follow_up_with}
                    </span>
                </div>
            )}

            <div className="pi-diagnosis__actions">
                <button
                    type="button"
                    className="pi-diagnosis__action-btn"
                    onClick={() => onLaunchWorkflow(report)}
                >
                    Turn into an action plan
                    <ArrowRight20Regular />
                </button>
            </div>
        </div>
    );
};

export default IntelligenceDiagnosisCard;
